import React, {useEffect, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import NobulaLogo from '../../../../../assets/icons/NobulaLogo.svg';
import {ProtectionType} from '../../../../incase-domain/presentationProtection';
import {loggedInUserSelector} from '../../../../store/selectors/auth.selector';
import {hasSlidesSelector, presentationStatusSelector} from '../../../../store/selectors/presentation.selectors';
import AAIEduLoginButton from '../../../../ui/components/AAIEdu/AAIEduLoginButton';
import ZoomLoginButton from '../../../../ui/components/AAIEdu/ZoomLoginButton';
import EmedikusLoginButton from '../../../../ui/components/AAIEdu/EmedikusLoginButton';
import Modal from '../../../../ui/components/common/Modal';
import Button from '../../../../incase-domain/components/button/Button';
import {hasCommonElements} from '../../../../util/array';
import GuestModal, {GuestCredentials} from './GuestModal';
import {handleLoginFormSubmit} from '../../../../util/loginFormUtils';
import {IFetchRoomProtection} from '../../../../incase-domain/rooms/room.player';
import {TypedTranslation} from '../../../../i18n';

interface UserLoginModalProps {
    protection: IFetchRoomProtection | undefined;
    findPresentation: (cr?: GuestCredentials) => void;
}

function UserAuthModal({protection, findPresentation}: UserLoginModalProps): JSX.Element {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const loggedInUser = useSelector(loggedInUserSelector);
    const presentationStatus = useSelector(presentationStatusSelector);
    const hasSlides = useSelector(hasSlidesSelector);

    const [loginModalOpen, setLoginModalOpen] = useState(false);
    const [loginLoader, setLoginLoader] = useState(false);
    const [loginError, setLoginError] = useState<TypedTranslation | undefined>();
    const [guestModalOpen, setGuestModalOpen] = useState(false);

    const protectionTypes = protection?.protectionTypes || [];

    const userLoginProtections: ProtectionType[] = [ProtectionType.REGISTERED_USERS, ProtectionType.SELECTED_USERS];
    const extraOptionsProtections: ProtectionType[] = [
        ProtectionType.AAI_EDU,
        ProtectionType.ZOOM,
        ProtectionType.EMEDIKUS,
        ProtectionType.GUEST,
        ProtectionType.IDENTIFICATION,
        ProtectionType.PASSWORD,
    ];

    const isUserLoginRequired = hasCommonElements(protectionTypes, userLoginProtections);
    const hasOnlyGuestSubsetProtections =
        protectionTypes.length > 0 &&
        protectionTypes.every((type) => [ProtectionType.IDENTIFICATION, ProtectionType.PASSWORD].includes(type));

    const showLoginForm = isUserLoginRequired;
    const showExtraLoginOptions = hasCommonElements(protectionTypes, extraOptionsProtections);

    const isPresentationDownloaded = presentationStatus.found && hasSlides;
    const isAuthenticatedOrPresentationLoaded = loggedInUser || isPresentationDownloaded;

    const shouldOpenLoginModal =
        (isUserLoginRequired || (showExtraLoginOptions && !hasOnlyGuestSubsetProtections)) && !isAuthenticatedOrPresentationLoaded;

    const shouldOpenGuestModal = hasOnlyGuestSubsetProtections && !isPresentationDownloaded;

    useEffect(() => {
        setLoginModalOpen(shouldOpenLoginModal);
        setGuestModalOpen(shouldOpenGuestModal);
    }, [shouldOpenLoginModal, shouldOpenGuestModal]);

    useEffect(() => {
        if (isUserLoginRequired && loggedInUser) {
            findPresentation();
        }
    }, [loggedInUser, isUserLoginRequired, findPresentation]);

    const closeModal = (): void => {
        setLoginModalOpen(false);
        navigate('/');
    };

    const handleGuestLogin = (): void => {
        if (protectionTypes.some((type) => [ProtectionType.IDENTIFICATION, ProtectionType.PASSWORD].includes(type))) {
            setGuestModalOpen(true);
            setLoginModalOpen(false);
        } else {
            findPresentation();
            setLoginModalOpen(false);
        }
    };

    return (
        <>
            <Modal
                isOpen={loginModalOpen}
                header={
                    <div className="c-credentialsModal__header u-mt-2">
                        <img src={NobulaLogo} alt="Nobula case logo" />
                    </div>
                }
                onCancel={closeModal}
                blockOverlayClose
            >
                <div className="c-credentialsModal__body" data-cy="credentials_modal">
                    <div className="c-credentialsModal__body_title">
                        <Trans i18nKey="common.welcome" />
                    </div>

                    <form
                        className={`c-form ${loginError ? 'is-invalid' : ''}`}
                        onSubmit={(e) => handleLoginFormSubmit(e, setLoginError, setLoginLoader, findPresentation, dispatch)}
                    >
                        {showLoginForm && (
                            <>
                                <div className="c-field">
                                    <label>
                                        <div className="c-credentialsModal__body_label">{t('form.emailOrusername')}</div>
                                        <div className="c-control">
                                            <input
                                                type="text"
                                                name="username"
                                                className="e-input"
                                                placeholder={t('form.emailOrusername')}
                                                data-cy="username"
                                            />
                                        </div>
                                    </label>
                                </div>

                                <div className="c-field">
                                    <label>
                                        <div className="c-credentialsModal__body_label">{t('form.password')}</div>
                                        <div className="c-control">
                                            <input
                                                type="password"
                                                name="password"
                                                className="e-input"
                                                placeholder={t('form.password')}
                                                data-cy="password"
                                            />
                                        </div>
                                    </label>
                                </div>

                                {loginError && <span className="c-form-help is-invalid">{t(loginError)}</span>}

                                <div className="u-mt-3">
                                    <Button
                                        fullWidth
                                        variant="solid"
                                        size="lg"
                                        loading={loginLoader}
                                        color="secondary"
                                        dataCy="loginSubmit"
                                        type="submit"
                                    >
                                        {t('form.login')}
                                    </Button>
                                </div>
                            </>
                        )}

                        {showExtraLoginOptions && (
                            <div className="u-flex u-flex-col u-mt-2 u-gap-3">
                                {showLoginForm && <div className="u-flex u-h-center ">{t('common.or').toUpperCase()}</div>}

                                {hasCommonElements(protectionTypes, [ProtectionType.AAI_EDU]) && <AAIEduLoginButton />}
                                {hasCommonElements(protectionTypes, [ProtectionType.ZOOM]) && <ZoomLoginButton />}
                                {hasCommonElements(protectionTypes, [ProtectionType.EMEDIKUS]) && <EmedikusLoginButton />}
                                {hasCommonElements(protectionTypes, [
                                    ProtectionType.GUEST,
                                    ProtectionType.IDENTIFICATION,
                                    ProtectionType.PASSWORD,
                                ]) && (
                                    <Button variant="soft" size="md" color="primary" onClick={handleGuestLogin} dataCy="guest_login_button">
                                        {t('form.continueAsGuest')}
                                    </Button>
                                )}
                            </div>
                        )}
                    </form>
                </div>
            </Modal>

            <GuestModal isOpen={guestModalOpen} onConfirm={findPresentation} protection={protection} />
        </>
    );
}

export default UserAuthModal;
